import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { fetchProfile, updateProfile, changePassword } from '../../lib/api';
import { AUTH_QUERY_KEY } from '../auth/useAuth';
import MiniAvatarCanvas from '../../components/MiniAvatarCanvas';
import { BASE_SPRITES, PALETTE_PRESETS } from '../avatar/avatarData';
import type { AvatarConfig } from '../avatar/avatarTypes';

const EYES_OPTIONS = ['normal', 'angry', 'glowing', 'cyclops', 'visor', 'x-eyes', 'heart', 'laser', 'hollow', 'spiral'];
const MOUTH_OPTIONS = ['smile', 'fangs', 'zigzag', 'open', 'flat', 'derp', 'scream', 'byte'];

const AVATAR_PRESETS: AvatarConfig[] = BASE_SPRITES.slice(0, 15).map((sprite, i) => {
  const palette = PALETTE_PRESETS[i % PALETTE_PRESETS.length];
  return {
    entityType: sprite.id,
    entityCategory: sprite.category,
    eyes: EYES_OPTIONS[i % EYES_OPTIONS.length],
    mouth: MOUTH_OPTIONS[i % MOUTH_OPTIONS.length],
    accessory: 'none',
    background: 'none',
    palette: palette.colors,
    gridSize: 14,
    name: sprite.name,
  };
});

export default function ProfilePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [avatar, setAvatar] = useState('default');
  const [username, setUsername] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [loading, setLoading] = useState(true);

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchProfile()
      .then((p) => {
        setAvatar(p.avatar);
        setUsername(p.username);
        setIsPublic(p.is_public ?? false);
      })
      .catch(() => toast.error('Failed to load profile'))
      .finally(() => setLoading(false));
  }, []);

  const selectAvatar = async (key: string, avatarConfig?: AvatarConfig) => {
    const prev = avatar;
    setAvatar(key);
    try {
      await updateProfile({
        avatar: avatarConfig ? 'custom' : key,
        ...(avatarConfig ? { avatar_config: avatarConfig as unknown as Record<string, unknown> } : {}),
      });
      qc.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
      toast.success('Avatar updated');
    } catch {
      setAvatar(prev);
      toast.error('Failed to update avatar');
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setChangingPassword(true);
    try {
      await changePassword({ currentPassword, newPassword });
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="malpedia-settings-page">
        <div className="malpedia-settings-container">Loading...</div>
      </div>
    );
  }

  return (
    <div className="malpedia-settings-page">
      <div className="malpedia-settings-container">
        <h1 className="malpedia-settings-title">Profile</h1>

        {/* Avatar Section */}
        <div className="malpedia-settings-section">
          <h2 className="malpedia-settings-section-title">Avatar</h2>
          <p className="malpedia-settings-hint">
            Choose an avatar that will appear in the header and next to your messages.
          </p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button
              className="malpedia-profile-save-btn"
              onClick={() => navigate('/avatar')}
            >
              Create Custom Avatar
            </button>
            <button
              className="malpedia-profile-save-btn"
              onClick={() => navigate('/avatars')}
            >
              View My Collection
            </button>
          </div>
          <div className="malpedia-avatar-picker-grid">
            {AVATAR_PRESETS.map((preset) => (
              <button
                key={preset.entityType}
                className={`malpedia-avatar-option${avatar === 'custom' ? '' : ''}`}
                onClick={() => selectAvatar('custom', preset)}
                title={preset.name}
              >
                <MiniAvatarCanvas config={preset as unknown as Record<string, unknown>} size={52} />
                <span className="malpedia-avatar-option-label">{preset.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Profile Visibility */}
        <div className="malpedia-settings-section">
          <h2 className="malpedia-settings-section-title">Profile Visibility</h2>
          <p className="malpedia-settings-hint">
            When public, other users can see your avatar collection.
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isPublic}
              onChange={async (e) => {
                const next = e.target.checked;
                setIsPublic(next);
                try {
                  await updateProfile({ avatar, is_public: next });
                  toast.success(next ? 'Profile is now public' : 'Profile is now private');
                } catch {
                  setIsPublic(!next);
                  toast.error('Failed to update visibility');
                }
              }}
            />
            <span>Make my profile public</span>
          </label>
        </div>

        {/* Password Section */}
        <div className="malpedia-settings-section">
          <h2 className="malpedia-settings-section-title">Change Password</h2>
          <form className="malpedia-password-form" onSubmit={handlePasswordSubmit}>
            <div className="malpedia-settings-row">
              <label className="malpedia-settings-label">Current Password</label>
              <input
                type="password"
                className="malpedia-settings-input"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>
            <div className="malpedia-settings-row">
              <label className="malpedia-settings-label">New Password</label>
              <input
                type="password"
                className="malpedia-settings-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>
            <div className="malpedia-settings-row">
              <label className="malpedia-settings-label">Confirm New Password</label>
              <input
                type="password"
                className="malpedia-settings-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>
            <button
              type="submit"
              className="malpedia-profile-save-btn"
              disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
            >
              {changingPassword ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
